import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackNewsletterSignup, trackDemoBooking } from '@/utils/analytics';
import logoOptimized from '@/assets/logo-optimized.webp';
import { CALENDLY_URL as CALENDLY_LINK } from '@/constants/links';
import { BackToTop } from '@/components/BackToTop';


export const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('newsletter-signup', {
        body: { email: email.trim().toLowerCase() }
      });

      if (error) throw error;

      // Track newsletter signup conversion
      trackNewsletterSignup();
      
      toast({
        title: "Subscribed!",
        description: "You've been added to our newsletter.",
      });
      setEmail('');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <footer
      className="bg-card border-t border-border/50"
      style={{ containIntrinsicSize: '0 400px', contentVisibility: 'auto' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4 group">
              <img 
                src={logoOptimized} 
                alt="Data Aviator" 
                className="h-12 w-auto transition-all duration-300 group-hover:scale-105 mix-blend-multiply dark:mix-blend-normal dark:brightness-110"
                width="120"
                height="48"
                loading="lazy"
              />
            </Link>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              Real-time intent data for smarter marketing. Reach in-market buyers with precision targeting.
            </p>
            
            {/* Newsletter Signup */}
            <form onSubmit={handleNewsletterSubmit} className="mb-4">
              <p className="text-sm font-medium text-foreground mb-2">Stay updated</p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 text-sm"
                  disabled={isSubmitting}
                  aria-label="Email address"
                  name="email"
                  autoComplete="email"
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  disabled={isSubmitting}
                  className="px-3"
                  aria-label="Subscribe to newsletter"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </form>

            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/data-aviator" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="https://x.com/dataaviator" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Twitter/X"
              >
                <Twitter className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/data-source" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Platform
                </Link>
              </li>
              <li>
                <Link to="/7-core-segments" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Segments
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/newsletter" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Newsletter
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/capabilities" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Capabilities
                </Link>
              </li>
              <li>
                <Link to="/vs-apollo" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Apollo Comparison
                </Link>
              </li>
              <li>
                <Link to="/facebook-lookalike-audiences" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Facebook Lookalikes Guide
                </Link>
              </li>
              <li>
                <a
                  href="https://docs.data-aviator.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1"
                >
                  API Docs
                </a>
              </li>
              <li>
                <a
                  href="https://radar-builder-pro.lovable.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors inline-block py-1"
                >
                  Build Your Audience
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/dpa" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Data Processing Agreement
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/acceptable-use" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Acceptable Use Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Privacy & Compliance */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-4">Privacy & Compliance</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/ccpa-privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  CCPA Rights
                </Link>
              </li>
              <li>
                <Link to="/gdpr-rights" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  GDPR Rights
                </Link>
              </li>
              <li>
                <Link to="/texas-privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Texas Privacy
                </Link>
              </li>
              <li>
                <Link to="/opt-out" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Do Not Sell My Info
                </Link>
              </li>
              <li>
                <Link to="/sla" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Service Level Agreement
                </Link>
              </li>
              <li>
                <Link to="/data-broker-registration" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  Data Broker Registration
                </Link>
              </li>
              <li>
                <Link to="/texas-personal-injury-compliance" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block py-1">
                  TX Personal Injury Compliance
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16 md:pb-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Data Aviator. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/disclaimer" className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors">
                Disclaimer
              </Link>
              <span className="text-muted-foreground/30">•</span>
              <Link to="/refund-policy" className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors">
                Refund Policy
              </Link>
              <span className="text-muted-foreground/30">•</span>
              <a 
                href="mailto:doug@data-aviator.com" 
                className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
              >
                doug@data-aviator.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <BackToTop />
    </>
  );
};
